import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { TrendingUp, Users, Clock, Sparkles, PiggyBank } from "lucide-react";

/**
 * Sector-aware ROI calculator.
 * Props:
 *   sectorSlugs: string[]  , all sectors linked to this product (first wins)
 *   kioskCost:   number    , one-time kiosk cost for payback calc
 */
export const ROICalculator = ({ sectorSlugs = [], kioskCost = 0 }) => {
  const { formatPrice } = useApp();
  const [cfg, setCfg] = useState(null);
  const [value, setValue] = useState("");
  const [result, setResult] = useState(null);
  const [hourlyCost, setHourlyCost] = useState(25);

  const slug = useMemo(() => sectorSlugs[0] || "hospitality", [sectorSlugs]);

  useEffect(() => {
    let mounted = true;
    api.get(`/roi/${slug}`)
      .then((r) => { if (mounted) { setCfg(r.data); setValue(r.data.placeholder); setHourlyCost(r.data.hourly_cost || 25); } })
      .catch(() => { if (mounted) setCfg(null); });
    return () => { mounted = false; };
  }, [slug]);

  useEffect(() => {
    if (!cfg || !value) { setResult(null); return; }
    const v = parseFloat(value) || 0;
    if (v <= 0) { setResult(null); return; }
    api.post("/roi/calculate", {
      sector_slug: slug,
      value: v,
      kiosk_cost: kioskCost,
      hourly_cost: hourlyCost,
    }).then((r) => setResult(r.data)).catch(() => setResult(null));
  }, [cfg, value, slug, kioskCost, hourlyCost]);

  if (!cfg) return null;

  const TypeIcon = cfg.type === "revenue" ? TrendingUp : cfg.type === "fte" ? Users : Clock;

  return (
    <div className="rounded-2xl border border-[#6FAA8A]/20 bg-gradient-to-br from-[#1F3A2E]/30 to-[#0B110F] p-6" data-testid="roi-calculator">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-full bg-[#6FAA8A]/20 grid place-items-center">
          <Sparkles className="w-4 h-4 text-[#6FAA8A]" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-[#6FAA8A] font-semibold">Jouw winst</div>
          <div className="text-lg font-semibold text-[#F4F1EA]">Wat levert de kiosk jou op?</div>
        </div>
      </div>

      <label className="block" data-testid="roi-input-label">
        <span className="text-xs text-[#A8B3AD]">{cfg.label_nl}</span>
        <div className="relative mt-1">
          <input
            type="number"
            min="0"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="input-field pr-20 w-full text-lg font-semibold"
            data-testid="roi-input"
            placeholder={cfg.placeholder}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#A8B3AD] pointer-events-none">
            {cfg.unit}
          </span>
        </div>
      </label>

      {cfg.type === "fte" && (
        <label className="block mt-3">
          <span className="text-xs text-[#A8B3AD]">Uurloon medewerker (€)</span>
          <input
            type="number" min="10" max="100" step="1"
            value={hourlyCost}
            onChange={(e) => setHourlyCost(parseFloat(e.target.value) || 25)}
            className="input-field mt-1"
            data-testid="roi-hourly-cost"
          />
        </label>
      )}

      <div className="text-[11px] text-[#6A7672] mt-3">{cfg.explanation_nl}</div>

      {result && result.monthly_benefit > 0 && (
        <div className="mt-5 space-y-3" data-testid="roi-result">
          <div className="flex items-center gap-3 p-4 rounded-xl bg-[#6FAA8A]/10 border border-[#6FAA8A]/30">
            <TypeIcon className="w-5 h-5 text-[#6FAA8A]" />
            <div className="flex-1">
              <div className="text-xs text-[#A8B3AD] uppercase tracking-wider">{cfg.result_label_nl}</div>
              <div className="text-xl font-bold text-[#6FAA8A]" data-testid="roi-result-text">
                {result.result_text_nl}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-xl bg-[#0B110F] border border-white/5 p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#6A7672]">Per maand</div>
              <div className="font-bold text-[#F4F1EA] text-lg" data-testid="roi-monthly">
                +{formatPrice(result.monthly_benefit)}
              </div>
            </div>
            <div className="rounded-xl bg-[#0B110F] border border-white/5 p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#6A7672]">Per jaar</div>
              <div className="font-bold text-[#F4F1EA] text-lg" data-testid="roi-yearly">
                +{formatPrice(result.yearly_benefit)}
              </div>
            </div>
          </div>

          {result.payback_months > 0 && kioskCost > 0 && (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-[#D97742]/10 border border-[#D97742]/30">
              <PiggyBank className="w-5 h-5 text-[#D97742]" />
              <div className="text-sm">
                <span className="text-[#A8B3AD]">Terugverdiend in </span>
                <span className="font-bold text-[#D97742]" data-testid="roi-payback">
                  {result.payback_months.toFixed(1)} maand{result.payback_months !== 1 ? "en" : ""}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ROICalculator;
